/**
 * Automatic product classification into the 4 homepage categories.
 *
 * Priority: TESORI > CAPITANO > GRANDE ROTTA > WANTED
 *
 * Source preference:
 *   1. Explicit Shopify tag override (tesori | capitano | grande-rotta | wanted)
 *      — also accepts `category:<slug>` and hyphen variants.
 *   2. Keyword match on tags, title, and productType (in this order).
 *
 * The function is pure — same input → same output — so it stays automatic
 * as soon as new Shopify products are synced.
 */

export const CATEGORY_SLUGS = ["tesori", "capitano", "grande-rotta", "wanted"];

// Keywords that promote a product to TESORI (highest priority)
const TESORI_KEYWORDS = [
    "special edition",
    "limited edition",
    "collector",
    "exclusive",
    "anniversary",
    "special",
    "premium",
    "gear 5 special",
    "limited",
    "deluxe",
];

// Captain names → CAPITANO
const CAPTAIN_KEYWORDS = ["luffy", "monkey d luffy", "monkey d. luffy"];

// Straw Hat crew (excluding Luffy → captain)
const CREW_KEYWORDS = [
    "zoro",
    "nami",
    "usopp",
    "sanji",
    "chopper",
    "tony tony chopper",
    "robin",
    "nico robin",
    "franky",
    "brook",
    "jinbe",
    "jimbei",
];

function normalize(s) {
    return (s || "").toString().toLowerCase().trim();
}

function containsAny(haystack, needles) {
    const h = normalize(haystack);
    return needles.some((n) => h.includes(n));
}

function tagIsCategoryOverride(tag) {
    const t = normalize(tag);
    // strip optional "category:" prefix
    const clean = t.startsWith("category:") ? t.slice(9) : t;
    if (CATEGORY_SLUGS.includes(clean)) return clean;
    // accept a couple of common alt spellings
    if (clean === "grande rotta") return "grande-rotta";
    return null;
}

/**
 * Classify a product into one of the 4 slugs.
 * @param {object} product - normalized product from shopify.js
 * @returns {"tesori"|"capitano"|"grande-rotta"|"wanted"}
 */
export function classifyProduct(product) {
    if (!product) return "wanted";
    const tags = Array.isArray(product.tags) ? product.tags : [];
    const title = product.name || product.character || "";
    const productType = product.productType || "";

    // 1) Explicit tag override — but respect the fixed priority afterwards
    let override = null;
    for (const tag of tags) {
        const cat = tagIsCategoryOverride(tag);
        if (cat) {
            override = cat;
            break;
        }
    }
    if (override) return override;

    // 2) Auto classification (haystack = tags + title + productType)
    const haystack = [...tags, title, productType].join(" | ");

    // Priority 1: TESORI (special / limited / exclusive / …)
    if (containsAny(haystack, TESORI_KEYWORDS)) return "tesori";

    // Priority 2: CAPITANO (Luffy)
    if (containsAny(haystack, CAPTAIN_KEYWORDS)) return "capitano";

    // Priority 3: GRANDE ROTTA (Straw Hat crew members)
    if (containsAny(haystack, CREW_KEYWORDS)) return "grande-rotta";

    // Priority 4: WANTED (everything else)
    return "wanted";
}

/**
 * Group a list of products by their auto-classified category.
 * @param {Array} products
 * @returns {{ tesori: any[], capitano: any[], "grande-rotta": any[], wanted: any[] }}
 */
export function groupProductsByCategory(products = []) {
    const groups = {
        tesori: [],
        capitano: [],
        "grande-rotta": [],
        wanted: [],
    };
    for (const p of products) {
        const slug = classifyProduct(p);
        (groups[slug] || groups.wanted).push(p);
    }
    return groups;
}

/**
 * Filter products by category slug using the automatic classifier.
 */
export function filterProductsByCategory(products = [], slug) {
    if (!slug) return products;
    return products.filter((p) => classifyProduct(p) === slug);
}
